// apps/web/src/pages/vendas/detalhe/itens-tabela.tsx — Fase 12, Plano 21 (PV-05, PV-06, D-25, D-38).
// Grade de itens do PV: leitura/resumo. Saldos são SEMPRE derivados pela API (nenhum campo editável).
// Essenciais: Item, Solicitado, Recebido, Saldo, expandir. Em produção/Aprovado/Fechado só >= 1280px
// (abaixo disso vão para a linha expandida). Preço, prazo, cor, material, roteiro e lotes: linha expandida.
import { Fragment, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, TrendingUp } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { PedidoVendaItemView } from '@pieltec/contracts';
import { classeSaldo, rotuloExcedente, rotuloSaldo } from '../../../lib/vendas/apresentacao';
import { formatarData, formatarMoeda, formatarQuantidade } from '../../../lib/vendas/formatacao';
import { LotesDoItem } from './lotes-do-item';
import { ItemAcoesMenu, type AcaoItemPv } from './item-acoes-menu';
import { PendenciaBadge, PendenciasBadges } from './pendencias-badges';

/** Acima deste total a grade renderiza só as linhas visíveis (ADR-005 D1). */
export const LIMITE_VIRTUALIZACAO_ITENS = 200;
const ALTURA_LINHA_ESTIMADA = 57;
const COLUNAS = 10;

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const SO_DESKTOP_LARGO = 'hidden xl:table-cell';

function qtd(valor: number, unidade: string): string {
  return `${formatarQuantidade(valor, unidade)} ${unidade}`;
}

function DadoExpandido({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/** Menu "⋯" por item (12-31): permissões do usuário e o comando escolhido; a página abre o Sheet/diálogo. */
export interface AcoesDoItem {
  readonly permissoes: readonly string[];
  readonly onAcao: (item: PedidoVendaItemView, acao: AcaoItemPv) => void;
}

interface AcoesGerarOp {
  readonly podeGerarOp?: boolean;
  readonly podeConsultarProducao?: boolean;
  readonly onOpGerada?: () => void;
  readonly acoesItem?: AcoesDoItem;
  /** Fase 17.3 (D-25): atalhos "Cadastrar peça" / "Cadastrar roteiro" na linha expandida; a página decide permissões e links. */
  readonly atalhosItem?: (item: PedidoVendaItemView) => React.ReactNode;
}

function LinhaItem({ item, aberto, onAlternar, podeGerarOp, podeConsultarProducao, onOpGerada, acoesItem, atalhosItem }: { item: PedidoVendaItemView; aberto: boolean; onAlternar: () => void } & AcoesGerarOp) {
  const encerrado = item.cancelado || item.saldoEncerrado;
  const excedente = rotuloExcedente(item.saldos.excedente, item.unidade);
  const idDetalhe = `item-${item.id}-detalhe`;

  return (
    <Fragment>
      <tr className={`border-t ${encerrado ? 'text-muted-foreground' : ''}`}>
        <td className="w-12 px-1 py-1">
          <button
            type="button"
            onClick={onAlternar}
            aria-expanded={aberto}
            aria-controls={idDetalhe}
            aria-label={`${aberto ? 'Recolher' : 'Expandir'} item ${item.numeroItem}`}
            className="inline-flex size-11 items-center justify-center rounded-md hover:bg-slate-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {aberto ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
          </button>
        </td>
        <td className="w-full max-w-0 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold" title={item.codigoItemCliente}>{item.codigoItemCliente}</span>
            {item.cancelado ? (
              <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">Cancelado</span>
            ) : item.saldoEncerrado ? (
              <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">Saldo encerrado</span>
            ) : null}
          </div>
          <div className="truncate text-muted-foreground">{item.descricao}</div>
        </td>
        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{qtd(item.saldos.solicitado, item.unidade)}</td>
        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
          <div>{qtd(item.saldos.recebido, item.unidade)}</div>
          {excedente && (
            <span className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              <TrendingUp className="size-3" aria-hidden="true" />
              {excedente}
            </span>
          )}
        </td>
        <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${SO_DESKTOP_LARGO}`}>{qtd(item.saldos.emProducao, item.unidade)}</td>
        <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${SO_DESKTOP_LARGO}`}>{qtd(item.saldos.aprovado, item.unidade)}</td>
        <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${SO_DESKTOP_LARGO}`}>{qtd(item.saldos.faturado, item.unidade)}</td>
        <td className={`px-3 py-2 text-right whitespace-nowrap ${classeSaldo(item.saldos.saldo)}`}>{rotuloSaldo(item.saldos.saldo, item.unidade)}</td>
        <td className="px-3 py-2">
          <PendenciasBadges pendencias={item.pendencias} />
        </td>
        <td className="w-12 px-1 py-1 text-right">
          {acoesItem && <ItemAcoesMenu item={item} permissoes={acoesItem.permissoes} onAcao={acoesItem.onAcao} />}
        </td>
      </tr>
      {aberto && (
        <tr id={idDetalhe} className="border-t bg-muted/50">
          <td colSpan={COLUNAS} className="px-4 py-3">
            <div className="flex flex-col gap-4">
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                <div className="contents xl:hidden">
                  <DadoExpandido rotulo="Em produção">{qtd(item.saldos.emProducao, item.unidade)}</DadoExpandido>
                  <DadoExpandido rotulo="Aprovado">{qtd(item.saldos.aprovado, item.unidade)}</DadoExpandido>
                  <DadoExpandido rotulo="Fechado">{qtd(item.saldos.faturado, item.unidade)}</DadoExpandido>
                </div>
                <DadoExpandido rotulo="Preço unitário">
                  {formatarMoeda(item.precoUnitario, 4)}
                  {item.precoOrigem && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {item.precoOrigem === 'TABELA' ? 'Preço da tabela' : 'Preço manual'}
                    </span>
                  )}
                </DadoExpandido>
                <DadoExpandido rotulo="Prazo">{formatarData(item.previsaoEntrega)}</DadoExpandido>
                <DadoExpandido rotulo="Cor">{item.cor ?? '—'}</DadoExpandido>
                <DadoExpandido rotulo="Material interno">
                  {item.materialCodigo ? `${item.materialCodigo}${item.materialNome ? ` — ${item.materialNome}` : ''}` : 'Não vinculado'}
                </DadoExpandido>
                <DadoExpandido rotulo="Roteiro">{item.roteiroId ? 'Definido' : 'Não definido'}</DadoExpandido>
              </dl>

              {atalhosItem?.(item)}

              {item.pendencias.length > 0 && (
                <section aria-label={`Pendências do item ${item.numeroItem}`} className="flex flex-col gap-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pendências</h3>
                  <ul className="flex flex-col gap-1">
                    {item.pendencias.map((pendencia) => (
                      <li key={pendencia.codigo} className="flex flex-wrap items-center gap-2">
                        <PendenciaBadge pendencia={pendencia} />
                        {pendencia.detalhe && <span className="text-sm text-muted-foreground">{pendencia.detalhe}</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <LotesDoItem
                lotes={item.lotes}
                unidade={item.unidade}
                numeroItem={item.numeroItem}
                roteiroId={item.roteiroId}
                encerrado={encerrado}
                podeGerarOp={podeGerarOp}
                podeConsultarProducao={podeConsultarProducao}
                onOpGerada={onOpGerada}
              />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Cabecalho() {
  return (
    <thead className="sticky top-0 z-10 bg-slate-100 text-left">
      <tr>
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Expandir</span>
        </th>
        <th scope="col" className={CABECALHO}>Item</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Solicitado</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Recebido</th>
        <th scope="col" className={`${CABECALHO} text-right ${SO_DESKTOP_LARGO}`}>Em produção</th>
        <th scope="col" className={`${CABECALHO} text-right ${SO_DESKTOP_LARGO}`}>Aprovado</th>
        <th scope="col" title="Quantidade incluída em fechamentos do pedido (reservada para expedição)" className={`${CABECALHO} text-right ${SO_DESKTOP_LARGO}`}>Fechado</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Saldo</th>
        <th scope="col" className={CABECALHO}>Pendências</th>
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Ações</span>
        </th>
      </tr>
    </thead>
  );
}

export function ItensTabela({ itens, podeGerarOp, podeConsultarProducao, onOpGerada, acoesItem, atalhosItem }: { itens: readonly PedidoVendaItemView[] } & AcoesGerarOp & { podeConsultarProducao: boolean }) {
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  const rolagemRef = useRef<HTMLDivElement>(null);
  const virtualizar = itens.length > LIMITE_VIRTUALIZACAO_ITENS;

  // Hook incondicional: com count 0 não faz nada quando a grade é pequena.
  const virtualizador = useVirtualizer({
    count: virtualizar ? itens.length : 0,
    getScrollElement: () => rolagemRef.current,
    estimateSize: () => ALTURA_LINHA_ESTIMADA,
    overscan: 8,
  });

  const alternar = (id: string) =>
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  if (itens.length === 0) {
    return <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhum item neste PV.</p>;
  }

  if (!virtualizar) {
    return (
      <table className="w-full text-sm">
        <caption className="sr-only">Itens do pedido de venda</caption>
        <Cabecalho />
        <tbody>
          {itens.map((item) => (
            <LinhaItem key={item.id} item={item} aberto={abertos.has(item.id)} onAlternar={() => alternar(item.id)} podeGerarOp={podeGerarOp} podeConsultarProducao={podeConsultarProducao} onOpGerada={onOpGerada} acoesItem={acoesItem} atalhosItem={atalhosItem} />
          ))}
        </tbody>
      </table>
    );
  }

  // Virtualizada: cada item é um <tbody> (uma ou duas linhas) medido; espaçadores mantêm a rolagem.
  const virtuais = virtualizador.getVirtualItems();
  const antes = virtuais.length > 0 ? (virtuais[0]?.start ?? 0) : 0;
  const depois = virtuais.length > 0 ? virtualizador.getTotalSize() - (virtuais[virtuais.length - 1]?.end ?? 0) : 0;

  return (
    <div ref={rolagemRef} className="max-h-[70vh] overflow-y-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">Itens do pedido de venda</caption>
        <Cabecalho />
        <tbody aria-hidden="true">
          <tr style={{ height: antes }} />
        </tbody>
        {virtuais.map((virtual) => {
          const item = itens[virtual.index];
          if (!item) return null;
          return (
            <tbody key={item.id} data-index={virtual.index} ref={virtualizador.measureElement}>
              <LinhaItem item={item} aberto={abertos.has(item.id)} onAlternar={() => alternar(item.id)} podeGerarOp={podeGerarOp} podeConsultarProducao={podeConsultarProducao} onOpGerada={onOpGerada} acoesItem={acoesItem} atalhosItem={atalhosItem} />
            </tbody>
          );
        })}
        <tbody aria-hidden="true">
          <tr style={{ height: depois }} />
        </tbody>
      </table>
    </div>
  );
}
